import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatHtml } from 'sanitize-text/sanitizers/HtmlSanitizer';
import { adjustLuminance } from 'visitor-ui-component-library/utils/adjustLuminance';
import { useAriaLiveContext } from 'visitor-ui-component-library/utils/aria-live/AriaLiveContext';
import { isInSDK } from 'visitor-ui-component-library/utils/isInSDK';
import { useTheme } from '../theming/contexts/ThemeContext';
import { getTextColor } from 'visitor-ui-component-library/utils/colors';
import { transformInlineCitations, useInlineCitations } from '../source-citations/hooks/useInlineCitations';
import { useWidgetModalOrContext } from '../widget-modal/WidgetModalContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const ContentTextWrapper = styled.div.withConfig({
  displayName: "ContentText__ContentTextWrapper"
})(["word-wrap:break-word;box-sizing:border-box;width:100%;a{font-weight:inherit;text-decoration:underline;}img{max-width:100%;height:auto;}annotation{display:none;}math[display='block']{padding:8px 0;}p:not(:last-child){margin-bottom:8px;}cite > a[href*='&hs-inline-citation='],cite > a[href*='?hs-inline-citation=']{color:", ";background-color:", ";&:hover{color:", ";background-color:", ";}}"], ({
  $textColor
}) => $textColor, ({
  $accentColor
}) => $accentColor, ({
  $textColor
}) => $textColor, ({
  $accentColor
}) => adjustLuminance($accentColor, 10));
const ContentText = props => {
  const {
    'aria-label': ariaLabel,
    browserWindowHeight,
    contentText,
    messageId,
    navigateToKnowledgeBaseArticle,
    onClickAction = () => {},
    shouldAnnounce,
    sources,
    isUngatedForInlineCitations = false
  } = props;
  const ref = useRef(null);
  const {
    announce
  } = useAriaLiveContext();
  const theme = useTheme();
  const {
    widgetAccent: accentColor
  } = theme;
  const textColor = getTextColor(accentColor);
  const {
    openModal
  } = useWidgetModalOrContext();
  const initializeInlineCitations = useInlineCitations({
    accentColor,
    browserWindowHeight: browserWindowHeight || 0,
    navigateToKnowledgeBaseArticle,
    openModal,
    sources
  });
  let mutatedContentText = contentText;
  if (sources && sources.length > 0 && isUngatedForInlineCitations) {
    // Turn those citation ids into links
    mutatedContentText = transformInlineCitations(mutatedContentText, sources);
  }
  useEffect(() => {
    if (shouldAnnounce) {
      announce({
        id: messageId || ariaLabel,
        text: ariaLabel
      });
    }
    if (sources && sources.length > 0 && isUngatedForInlineCitations) {
      initializeInlineCitations();
    }
  }, [announce, ariaLabel, initializeInlineCitations, isUngatedForInlineCitations, messageId, openModal, shouldAnnounce, sources]);
  return /*#__PURE__*/_jsx(ContentTextWrapper, {
    $accentColor: accentColor,
    $textColor: textColor,
    children: /*#__PURE__*/_jsx("div", {
      ref: ref,
      "aria-label": ariaLabel,
      dir: "auto",
      "data-test-id": "primary-message-content"
      // eslint-disable-next-line react/no-danger
      ,
      dangerouslySetInnerHTML: {
        __html: formatHtml(mutatedContentText, {
          shouldPreserveMathML: true,
          shouldPreserveNewlines: true,
          shouldOpenLinksInNewTab: isInSDK(),
          allowedDomainsForIframe: [/^\/?\/?play\.vidyard\.com\/[a-zA-Z0-9]*\.html$/gm, /(^|\.)hubspot\.com/gm]
        })
      },
      onClick: onClickAction
    })
  });
};
ContentText.displayName = 'ContentText';
ContentText.propTypes = {
  'aria-label': PropTypes.string,
  contentText: PropTypes.string.isRequired,
  messageId: PropTypes.string,
  onClickAction: PropTypes.func,
  shouldAnnounce: PropTypes.bool
};
export default ContentText;